import { PLATFORM_SUSPENDED_ERROR_CODE } from './PlatformSuspendedScreen';

// VOOP-2473: while the backend container is being redeployed, nginx answers API calls with 502
// (the backend is unreachable). Instead of every screen failing with its own error, the whole app is
// covered with the same VOOPTY loader the page shows while it first opens (#index-loader in
// index.html), and one shared probe waits until the backend answers again.
//
// Plain DOM on purpose, like PlatformSuspendedScreen: triggered from RestApiService, outside React,
// and has to cover both bundles regardless of which route/layout is mounted.

const SCREEN_ID = 'backend-unavailable-screen';
export const PROBE_INTERVAL_MS = 3000;
// The .eye animations in index.html run once (blink twice, look around); restarting them keeps the
// exact same animation alive for as long as the deploy takes.
const ANIMATION_RESTART_MS = 4000;

// 503 is only "unavailable" when it isn't the deliberate platform-suspended answer, which has its own
// screen. 504 is left out on purpose: it means a slow request (proxy/client timeout), not a deploy.
export function isBackendUnavailable(status: number, body?: any): boolean {
  if (status === 502) return true;
  return status === 503 && body?.errorCode !== PLATFORM_SUSPENDED_ERROR_CODE;
}

// Is the backend answering again? A cheap public call the app already makes on start
// (ServiceLocator.checkDomain). A platform-suspended 503 counts as "back" - the request that follows
// then shows its own screen.
export async function isBackendBack(apiBaseUrl: string): Promise<boolean> {
  const response = await fetch(`${apiBaseUrl}/domains/${window.location.hostname}`, {
    cache: 'no-store',
    credentials: 'include',
  });
  if (response.status !== 502 && response.status !== 503) return true;
  const body = await response.json().catch(() => undefined);
  return !isBackendUnavailable(response.status, body);
}

const createEyes = (): HTMLElement => {
  const eyes = document.createElement('div');
  eyes.className = 'eyes';
  ['eye eye-left', 'eye eye-right'].forEach((className) => {
    const eye = document.createElement('div');
    eye.className = className;
    eyes.appendChild(eye);
  });
  return eyes;
};

let screen: HTMLElement | null = null;
let restartTimer: ReturnType<typeof setInterval> | null = null;

export function showBackendUnavailableScreen(): void {
  if (typeof document === 'undefined' || screen) return;

  screen = document.createElement('div');
  screen.id = SCREEN_ID;
  screen.setAttribute('role', 'status');
  screen.setAttribute('aria-label', 'Voopty');
  // Same box as #index-loader in index.html.
  Object.assign(screen.style, {
    position: 'fixed',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    width: '100%',
    height: '100vh',
    zIndex: '2147483646',
    textAlign: 'center',
    backgroundColor: '#fff',
  });

  const logo = document.createElement('div');
  logo.className = 'logo-animation';
  const logoBg = document.createElement('div');
  logoBg.className = 'logo-bg';
  logo.appendChild(createEyes());
  logo.appendChild(logoBg);
  screen.appendChild(logo);
  document.body.appendChild(screen);

  restartTimer = setInterval(() => {
    const eyes = logo.querySelector('.eyes');
    if (eyes) logo.replaceChild(createEyes(), eyes);
  }, ANIMATION_RESTART_MS);
}

export function hideBackendUnavailableScreen(): void {
  if (restartTimer) clearInterval(restartTimer);
  restartTimer = null;
  screen?.remove();
  screen = null;
}

let waitingForBackend: Promise<void> | null = null;

// Shows the loader and resolves once `probe` says the backend is back. Every request that hits a 502
// while it's down shares this one wait (and one probe loop), then the loader goes away.
export function waitForBackend(probe: () => Promise<boolean>, intervalMs: number = PROBE_INTERVAL_MS): Promise<void> {
  if (waitingForBackend) return waitingForBackend;

  showBackendUnavailableScreen();
  waitingForBackend = new Promise<void>((resolve) => {
    const check = () => {
      probe()
        .catch(() => false)
        .then((isUp) => {
          if (isUp) {
            hideBackendUnavailableScreen();
            waitingForBackend = null;
            resolve();
          } else {
            setTimeout(check, intervalMs);
          }
        });
    };
    setTimeout(check, intervalMs);
  });
  return waitingForBackend;
}
